import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Wallet, RefreshCw, ChevronDown, ChevronRight, Info } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useQontoSilentSync } from "../hooks/useQontoSilentSync";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { formatCurrency, formatCurrencyFull } from "../utils/formatters";
import { COLORS, TVA_DEDUCTIBLE_PAR_NATURE } from "../utils/constants";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";

// Liste des catégories de base (mise à jour avec nouvelles catégories TVA)
const BASE_CATEGORIES = [
  "Matières premières",
  "Achat de marchandises",
  "Fournitures bureau",
  "Loyer local",
  "Matériel informatique",
  "Abonnement logiciel",
  "Abonnement internet",
  "Abonnement téléphone",
  "Assurance pro",
  "Frais de communication",
  "Frais de déplacement",
  "Frais bancaires",
  "Cotisation Urssaf",
  "CFE",
  "Matériel professionnel",
  "Véhicule utilitaire",
  "Véhicule de tourisme",
  "Carburant essence",
  "Carburant gazole",
  "Électricité véhicule",
  "Repas d'affaires",
  "Cadeaux clients",
  "Hébergement (hôtel)",
  "Formation professionnelle",
  "Achats divers", // fallback quand auto-détection Qonto ne trouve pas le fournisseur
];

// Depenses Page with Abonnements
export const DepensesPage = () => {
  const { user } = useAuth();
  const [depenses, setDepenses] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomSuggestions, setShowCustomSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("depenses");
  const [expandedMonths, setExpandedMonths] = useState({});
  const [statutTva, setStatutTva] = useState(null);
  const [formData, setFormData] = useState({ 
    date: new Date().toISOString().split('T')[0], 
    nature: "", 
    fournisseur: "", 
    montant: "", 
    statut: "payé", 
    recurrence: "ponctuel",
    // Champs TVA
    taux_tva: null,
    montant_tva: null,
    taux_deductible: null,
    tva_deductible: null,
    // Fournisseur en franchise de TVA (auto-entrepreneur ≤ 36800€ CA, association, etc.)
    // Quand true → on ignore complètement le calcul TVA même si la nature en suggère.
    tva_franchise: false,
  });
  const api = useApi();

  useEffect(() => { loadData(); }, []);
  // Synchro Qonto silencieuse à l'ouverture (recharge les dépenses ~3 sec après si Qonto a livré du neuf)
  useQontoSilentSync(() => loadData());

  const loadData = async () => {
    try {
      const [depensesRes, categoriesRes, dashboardRes] = await Promise.all([
        api.get("/depenses"), 
        api.get("/depenses/categories"),
        api.get("/dashboard")
      ]);
      setDepenses(depensesRes.data);
      setCustomCategories(categoriesRes.data.custom_categories || []);
      setStatutTva(dashboardRes.data.statut_tva || null);
    } catch (err) { toast.error("Erreur de chargement"); }
    setLoading(false);
  };

  // Calculer TVA quand nature ou montant change (pour assujettis)
  const calculerTva = (nature, montantTtc, isFranchise = false) => {
    if (!statutTva?.assujetti || !nature || !montantTtc || isFranchise) {
      return { taux_tva: null, montant_tva: null, taux_deductible: null, tva_deductible: null };
    }
    
    const regle = TVA_DEDUCTIBLE_PAR_NATURE[nature];
    if (!regle) {
      return { taux_tva: 20, montant_tva: 0, taux_deductible: 100, tva_deductible: 0 };
    }
    
    const montant = parseFloat(montantTtc) || 0;
    const tauxTva = regle.taux;
    const montantTva = tauxTva > 0 ? montant - (montant / (1 + tauxTva / 100)) : 0;
    const tauxDeductible = regle.deductible;
    const tvaDeductible = montantTva * (tauxDeductible / 100);
    
    return {
      taux_tva: tauxTva,
      montant_tva: Math.round(montantTva * 100) / 100,
      taux_deductible: tauxDeductible,
      tva_deductible: Math.round(tvaDeductible * 100) / 100
    };
  };

  // Toggle franchise TVA : recalcule la TVA (ou la met à null si franchise activée)
  const handleFranchiseToggle = (checked) => {
    const tvaData = calculerTva(formData.nature, formData.montant, checked);
    setFormData({ ...formData, tva_franchise: checked, ...tvaData });
  };

  // Handler pour changement de nature
  const handleNatureChange = (value) => {
    const tvaData = calculerTva(value, formData.montant, formData.tva_franchise);
    setFormData({ ...formData, nature: value, ...tvaData });
    if (value !== "personnalise") setCustomCategory("");
  };

  // Handler pour changement de montant
  const handleMontantChange = (value) => {
    const tvaData = calculerTva(formData.nature, value, formData.tva_franchise);
    setFormData({ ...formData, montant: value, ...tvaData });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nature = formData.nature === "personnalise" ? customCategory : formData.nature;
    if (!nature || nature === "") {
      toast.error("Veuillez sélectionner une nature");
      return;
    }
    if (!formData.montant || parseFloat(formData.montant) <= 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }
    
    // Recalculer TVA avec la nature finale (au cas où personnalisé)
    const tvaData = calculerTva(nature, formData.montant, formData.tva_franchise);
    const dataToSend = { 
      ...formData, 
      nature, 
      montant: parseFloat(formData.montant),
      ...tvaData
    };
    
    try {
      if (editingId) {
        await api.put(`/depenses/${editingId}`, dataToSend);
        toast.success("Dépense modifiée");
      } else {
        await api.post("/depenses", dataToSend);
        toast.success("Dépense ajoutée");
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        date: new Date().toISOString().split('T')[0], 
        nature: "", 
        fournisseur: "", 
        montant: "", 
        statut: "payé", 
        recurrence: "ponctuel",
        taux_tva: null,
        montant_tva: null,
        taux_deductible: null,
        tva_deductible: null
      });
      setCustomCategory("");
      loadData();
    } catch (err) { toast.error("Erreur"); }
  };

  const handleEdit = (depense) => {
    const isCustom = !BASE_CATEGORIES.includes(depense.nature);
    setFormData({ 
      date: depense.date, 
      nature: isCustom ? "personnalise" : depense.nature, 
      fournisseur: depense.fournisseur || "", 
      montant: depense.montant.toString(), 
      statut: depense.statut, 
      recurrence: depense.recurrence,
      // Champs TVA (importants pour les factures Qonto où Qonto a fourni la TVA explicitement)
      taux_tva: depense.taux_tva ?? null,
      montant_tva: depense.montant_tva ?? null,
      taux_deductible: depense.taux_deductible ?? null,
      tva_deductible: depense.tva_deductible ?? null,
      tva_franchise: !!depense.tva_franchise,
    });
    if (isCustom) setCustomCategory(depense.nature);
    setEditingId(depense.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette dépense ?")) {
      try { 
        await api.delete(`/depenses/${id}`); 
        toast.success("Dépense supprimée"); 
        loadData(); 
      } catch (err) { 
        const message = err.response?.data?.detail || "Erreur";
        toast.error(message);
      }
    }
  };

  // Filtrer les suggestions personnalisées
  const filteredCustomSuggestions = customCategories.filter(
    c => c.toLowerCase().includes(customCategory.toLowerCase()) && c !== customCategory
  );

  const depensesPonctuelles = depenses.filter(d => d.recurrence === "ponctuel");
  const abonnements = depenses.filter(d => d.recurrence === "mensuel" || d.recurrence === "annuel");
  
  // Mois courant pour le filtrage
  const now = new Date();
  const moisCourant = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  
  // Reste à payer CE MOIS = dépenses à payer avec date dans le mois courant uniquement
  const resteAPayer = depenses.filter(d => d.statut === "à payer" && d.date.startsWith(moisCourant)).reduce((sum, d) => sum + d.montant, 0);
  // Total annuel = uniquement l'année en cours (les imports Qonto peuvent contenir des dates antérieures)
  const anneeCourante = String(now.getFullYear());
  const totalDepenses = depenses.filter(d => (d.date || "").startsWith(anneeCourante)).reduce((sum, d) => sum + d.montant, 0);

  // Grouper par mois
  const groupByMonth = (items) => {
    const groups = {};
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    items.forEach(item => {
      const month = item.date.substring(0, 7);
      if (!groups[month]) groups[month] = [];
      groups[month].push(item);
    });
    
    // Trier les mois du plus récent au plus ancien
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, items]) => ({
        month,
        items: items.sort((a, b) => b.date.localeCompare(a.date)),
        total: items.reduce((sum, i) => sum + i.montant, 0),
        isCurrent: month === currentMonth
      }));
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const renderGroupedTable = (items) => {
    const grouped = groupByMonth(items);
    
    return (
      <div className="divide-y">
        {grouped.map(({ month, items, total, isCurrent }) => (
          <div key={month}>
            {/* Header du mois */}
            <button
              onClick={() => !isCurrent && toggleMonth(month)}
              className={`w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors ${isCurrent ? 'cursor-default' : 'cursor-pointer'}`}
              style={{ backgroundColor: isCurrent ? COLORS.bg : undefined }}
            >
              <div className="flex items-center gap-3">
                {!isCurrent && (
                  expandedMonths[month] ? <ChevronDown size={18} /> : <ChevronRight size={18} />
                )}
                <span className="font-semibold" style={{ color: COLORS.text }}>
                  {formatMonth(month)}
                  {isCurrent && <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: COLORS.principale + '30', color: COLORS.text }}>Mois en cours</span>}
                </span>
                <span className="text-sm text-muted-foreground">({items.length} dépense{items.length > 1 ? 's' : ''})</span>
              </div>
              <span className="font-bold" style={{ color: COLORS.error }}>-{formatCurrency(total)}</span>
            </button>
            
            {/* Détails - toujours visible pour le mois en cours, sinon selon état */}
            {(isCurrent || expandedMonths[month]) && (
              <div className={`${!isCurrent ? 'bg-muted/10' : ''}`}>
                {items.map((depense) => (
                  <div key={depense.id} className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-t border-dashed hover:bg-muted/20 transition-colors gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => handleEdit(depense)} className="p-1.5 hover:bg-muted rounded-lg"><Edit size={14} /></button>
                        <button onClick={() => handleDelete(depense.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-lg"><Trash2 size={14} /></button>
                      </div>
                      <div className={`min-w-0 ${!isCurrent ? 'text-sm text-muted-foreground' : ''}`}>
                        <span className="text-xs text-muted-foreground mr-2">{new Date(depense.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                        <span className={`font-medium capitalize ${!isCurrent ? 'text-muted-foreground' : ''}`}>{depense.nature}</span>
                        {depense.auto_urssaf && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-purple-100 text-purple-700" title="Créée automatiquement à partir de la fréquence de déclaration. Marque-la comme payée quand tu l'as réglée.">
                            🤖 auto
                          </span>
                        )}
                        {depense.fournisseur && <span className="text-muted-foreground ml-1 text-sm hidden sm:inline">({depense.fournisseur})</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 justify-end sm:justify-start ml-auto sm:ml-0">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${depense.statut === "payé" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {depense.statut}
                      </span>
                      <span className={`font-mono font-medium whitespace-nowrap ${!isCurrent ? 'text-sm text-muted-foreground' : ''}`} style={{ color: isCurrent ? COLORS.error : undefined }}>
                        -{formatCurrencyFull(depense.montant)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="depenses-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{color: COLORS.text}}>MES DÉPENSES</h1>
            <span className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold" style={{backgroundColor: COLORS.bg, color: COLORS.accent}}>{depenses.length}</span>
          </div>
          <button onClick={() => { setShowForm(true); setEditingId(null); setFormData({ date: new Date().toISOString().split('T')[0], nature: "", fournisseur: "", montant: "", statut: "payé", recurrence: activeTab === "abonnements" ? "mensuel" : "ponctuel" }); setCustomCategory(""); }}
            data-testid="add-depense-btn" className="flex items-center gap-2 px-4 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
            <Plus size={20} /> Nouvelle dépense
          </button>
        </div>

        {/* Reste à payer banner */}
        {resteAPayer > 0 && (
          <div className="rounded-xl p-4" style={{backgroundColor: COLORS.bg, border: `2px solid ${COLORS.warning}`}}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">💸</span>
              <div>
                <div className="text-sm text-muted-foreground">Reste à payer ce mois</div>
                <div className="text-2xl font-bold" style={{color: COLORS.error}}>{formatCurrency(resteAPayer)}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card rounded-xl border p-5" style={{borderLeft: `4px solid ${COLORS.error}`}}>
            <div className="text-sm text-muted-foreground mb-1">Abonnements mensuels</div>
            <div className="text-2xl font-bold" style={{color: COLORS.error}}>{formatCurrency(abonnements.filter(a => a.recurrence === "mensuel").reduce((s, a) => s + a.montant, 0))}</div>
          </div>
          <div className="bg-card rounded-xl border p-5" style={{borderLeft: `4px solid ${COLORS.warning}`}}>
            <div className="text-sm text-muted-foreground mb-1">Total {new Date().getFullYear()}</div>
            <div className="text-2xl font-bold" style={{color: COLORS.warning}}>{formatCurrency(totalDepenses)}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button onClick={() => setActiveTab("depenses")} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === "depenses" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Dépenses ({depensesPonctuelles.length})
          </button>
          <button onClick={() => setActiveTab("abonnements")} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === "abonnements" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <RefreshCw size={16} className="inline mr-1" />Abonnements ({abonnements.length})
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl p-6 w-full max-w-md animate-slide-up max-h-[90vh] overflow-auto">
              <h2 className="text-xl font-bold mb-4" style={{color: COLORS.text}}>{editingId ? "Modifier" : "Nouvelle"} {formData.recurrence !== "ponctuel" ? "abonnement" : "dépense"}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="block text-sm font-medium mb-2">Date</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nature</label>
                  <Select 
                    value={formData.nature} 
                    onValueChange={handleNatureChange}
                  >
                    <SelectTrigger className="w-full h-11">
                      <SelectValue placeholder="Sélectionner une nature..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Catégories courantes</SelectLabel>
                        <SelectItem value="Matières premières">Matières premières</SelectItem>
                        <SelectItem value="Achat de marchandises">Achat de marchandises</SelectItem>
                        <SelectItem value="Fournitures bureau">Fournitures bureau</SelectItem>
                        <SelectItem value="Loyer local">Loyer local</SelectItem>
                        <SelectItem value="Matériel informatique">Matériel informatique</SelectItem>
                        <SelectItem value="Abonnement logiciel">Abonnement logiciel</SelectItem>
                        <SelectItem value="Abonnement internet">Abonnement internet</SelectItem>
                        <SelectItem value="Abonnement téléphone">Abonnement téléphone</SelectItem>
                        <SelectItem value="Assurance pro">Assurance pro</SelectItem>
                        <SelectItem value="Frais de communication">Frais de communication</SelectItem>
                        <SelectItem value="Frais de déplacement">Frais de déplacement</SelectItem>
                        <SelectItem value="Frais bancaires">Frais bancaires</SelectItem>
                        <SelectItem value="Cotisation Urssaf">Cotisation Urssaf</SelectItem>
                        <SelectItem value="CFE">CFE</SelectItem>
                        <SelectItem value="Achats divers">Achats divers</SelectItem>
                      </SelectGroup>
                      {statutTva?.assujetti && (
                        <SelectGroup>
                          <SelectLabel>Véhicules & Carburant</SelectLabel>
                          <SelectItem value="Véhicule utilitaire">Véhicule utilitaire</SelectItem>
                          <SelectItem value="Véhicule de tourisme">Véhicule de tourisme</SelectItem>
                          <SelectItem value="Carburant essence">Carburant essence</SelectItem>
                          <SelectItem value="Carburant gazole">Carburant gazole</SelectItem>
                          <SelectItem value="Électricité véhicule">Électricité véhicule</SelectItem>
                        </SelectGroup>
                      )}
                      {statutTva?.assujetti && (
                        <SelectGroup>
                          <SelectLabel>Frais professionnels</SelectLabel>
                          <SelectItem value="Matériel professionnel">Matériel professionnel</SelectItem>
                          <SelectItem value="Repas d'affaires">Repas d'affaires</SelectItem>
                          <SelectItem value="Cadeaux clients">Cadeaux clients</SelectItem>
                          <SelectItem value="Hébergement (hôtel)">Hébergement (hôtel)</SelectItem>
                          <SelectItem value="Formation professionnelle">Formation professionnelle</SelectItem>
                        </SelectGroup>
                      )}
                      {customCategories.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Mes catégories</SelectLabel>
                          {customCategories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                        </SelectGroup>
                      )}
                      <SelectGroup>
                        <SelectItem value="personnalise">➕ Ajouter une nature...</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {formData.nature === "personnalise" && (
                  <div className="relative">
                    <label className="block text-sm font-medium mb-2">Nature personnalisée</label>
                    <input 
                      type="text" 
                      value={customCategory} 
                      onChange={(e) => { setCustomCategory(e.target.value); setShowCustomSuggestions(true); }}
                      onFocus={() => setShowCustomSuggestions(true)}
                      className="w-full h-11 px-4 rounded-lg border border-input bg-background" 
                      placeholder="Ex: Licence logiciel" 
                      required 
                    />
                    {showCustomSuggestions && filteredCustomSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-card border rounded-lg shadow-lg max-h-40 overflow-auto">
                        {filteredCustomSuggestions.map((c, i) => (
                          <button key={i} type="button" onClick={() => { setCustomCategory(c); setShowCustomSuggestions(false); }}
                            className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm">{c}</button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Cette nature sera mémorisée pour tes prochaines dépenses</p>
                  </div>
                )}
                <div><label className="block text-sm font-medium mb-2">Fournisseur (optionnel)</label>
                  <input type="text" value={formData.fournisseur} onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" />
                </div>
                <div><label className="block text-sm font-medium mb-2">Montant TTC (€)</label>
                  <input type="number" value={formData.montant} onChange={(e) => handleMontantChange(e.target.value)} className="w-full h-11 px-4 rounded-lg border border-input bg-background" min="0" step="0.01" required />
                </div>

                {/* Toggle franchise TVA — uniquement visible si user assujetti TVA */}
                {statutTva?.assujetti && (
                  <label className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30 cursor-pointer hover:bg-muted/50 transition" data-testid="depense-franchise-tva-toggle">
                    <input
                      type="checkbox"
                      checked={!!formData.tva_franchise}
                      onChange={(e) => handleFranchiseToggle(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-input"
                    />
                    <div className="flex-1 text-sm">
                      <div className="font-medium">Ce fournisseur est en franchise de TVA</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        À cocher si le fournisseur est auto-entrepreneur en franchise, association, prestataire UE/hors UE, etc. (= aucune TVA sur la facture). Tu ne pourras alors rien récupérer en TVA.
                      </div>
                    </div>
                  </label>
                )}
                
                {/* Section TVA pour assujettis */}
                {statutTva?.assujetti && !formData.tva_franchise && formData.nature && formData.nature !== "personnalise" && formData.montant && (
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: COLORS.bg }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Info size={16} style={{ color: COLORS.accent }} />
                      <span className="font-medium text-sm" style={{ color: COLORS.accent }}>TVA déductible</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-muted-foreground">Taux TVA :</span>
                        <span className="ml-2 font-medium">{formData.taux_tva || 0}%</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Montant TVA :</span>
                        <span className="ml-2 font-medium">{formatCurrency(formData.montant_tva || 0)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">% récupérable :</span>
                        <span className="ml-2 font-medium" style={{ color: formData.taux_deductible === 0 ? COLORS.error : formData.taux_deductible === 100 ? COLORS.success : COLORS.warning }}>
                          {formData.taux_deductible || 0}%
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">TVA déductible :</span>
                        <span className="ml-2 font-bold" style={{ color: COLORS.success }}>{formatCurrency(formData.tva_deductible || 0)}</span>
                      </div>
                    </div>
                    
                    {/* Message d'info si présent */}
                    {TVA_DEDUCTIBLE_PAR_NATURE[formData.nature]?.message && (
                      <div className="text-xs p-2 rounded" style={{ backgroundColor: COLORS.warning + '20', color: COLORS.text }}>
                        ℹ️ {TVA_DEDUCTIBLE_PAR_NATURE[formData.nature].message}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-2">Statut</label>
                    <select 
                      value={formData.statut} 
                      onChange={(e) => setFormData({ ...formData, statut: e.target.value })} 
                      className="w-full h-11 px-4 rounded-lg border border-input bg-background appearance-none"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      <option value="payé">Payé</option><option value="à payer">À payer</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium mb-2">Récurrence</label>
                    <select 
                      value={formData.recurrence} 
                      onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })} 
                      className="w-full h-11 px-4 rounded-lg border border-input bg-background appearance-none"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      <option value="ponctuel">Ponctuel</option><option value="mensuel">Mensuel</option><option value="annuel">Annuel</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-11 rounded-lg border border-input hover:bg-muted transition-colors">Annuler</button>
                  <button type="submit" className="flex-1 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">{editingId ? "Modifier" : "Ajouter"}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-card rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" /></div>
          ) : (activeTab === "depenses" ? depensesPonctuelles : abonnements).length === 0 ? (
            <div className="p-12 text-center text-muted-foreground"><Wallet className="mx-auto mb-4 opacity-50" size={48} /><p>Aucune {activeTab === "abonnements" ? "abonnement" : "dépense"}</p></div>
          ) : (
            renderGroupedTable(activeTab === "depenses" ? depensesPonctuelles : abonnements)
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
